/**
 * Remastered Digby, built from a small rig of lit shapes: helmet and lamp, face, shirt, belt,
 * trousers, boots. The same parts pose into standing, blinking, tapping and a four-frame run, so
 * every frame shares one light and one outline. Facing left is the mirror of facing right. Pure.
 *
 * He keeps his own colours in every cave, as a C64 hardware sprite would, so he always stands out
 * from the cave around him.
 */

import type { Ramp, RGB } from './color';
import { rampOf } from './color';
import { Px } from './px';

export interface DigbyPalette {
  readonly helmet: Ramp;
  readonly shirt: Ramp;
  readonly trousers: Ramp;
  readonly boots: Ramp;
  readonly skin: Ramp;
  readonly ink: RGB;
}

export function digbyPalette(ink: RGB): DigbyPalette {
  return {
    helmet: rampOf([238, 188, 44]),
    shirt: rampOf([58, 104, 220]),
    trousers: rampOf([128, 84, 46]),
    boots: rampOf([84, 66, 62]),
    skin: rampOf([246, 198, 152]),
    ink,
  };
}

const LAMP: RGB = [255, 252, 232];

function lamp(p: Px, c: DigbyPalette, x: number, y: number): void {
  p.set(x, y, LAMP);
  p.set(x + 1, y, LAMP);
  p.set(x, y + 1, c.skin[4]);
  p.set(x + 1, y + 1, c.helmet[3]);
}

export interface FrontPose {
  readonly blink?: boolean;
  readonly tap?: boolean;
}

/** Facing the player: standing, blinking, or tapping a foot. */
export function digbyFront(c: DigbyPalette, pose: FrontPose = {}): Px {
  const p = new Px();
  const tap = pose.tap === true;
  p.bar(4, 14, 6, 15, c.boots, 0, 3);
  p.bar(9, tap ? 13 : 14, 11, tap ? 14 : 15, c.boots, 0, 3);
  p.bar(5, 12, 6, 13, c.trousers, 1, 3);
  p.bar(9, 12, 10, tap ? 12 : 13, c.trousers, 1, 3);
  p.bar(4, 8, 11, 11, c.shirt, 1, 4);
  p.fill(4, 11, 11, 11, c.trousers[0]);
  p.set(7, 11, c.helmet[4]);
  p.set(8, 11, c.helmet[2]);
  p.bar(2, 8, 3, 10, c.shirt, 1, 3);
  p.bar(12, 8, 13, 10, c.shirt, 0, 3);
  p.fill(2, 11, 3, 11, c.skin[2]);
  p.fill(12, 11, 13, 11, c.skin[1]);
  p.dome(8, 5.7, 3.7, 2.7, c.skin, 1, 4, (_x, y) => y >= 4);
  for (const x of [6, 9]) {
    if (pose.blink) p.set(x, 6, c.skin[0]);
    else p.fill(x, 5, x, 6, c.ink);
  }
  p.set(7, 7, c.skin[0]);
  p.set(8, 7, c.skin[0]);
  p.dome(8, 3.5, 4.8, 3.3, c.helmet, 1, 4, (_x, y) => y <= 2);
  p.bar(2, 3, 13, 3, c.helmet, 0, 3);
  lamp(p, c, 7, 0);
  p.outline(c.ink);
  return p;
}

/** Running right, frame 0-3: stride, pass, stride, pass, with the arm swinging and a small bob. */
export function digbyRun(c: DigbyPalette, frame: number): Px {
  const p = new Px();
  const pass = frame % 2 === 1;
  const bob = pass ? -1 : 0;
  if (pass) {
    p.bar(7, 12, 9, 13, c.trousers, 1, 3);
    if (frame === 1) p.bar(7, 14, 10, 15, c.boots, 0, 3);
    else {
      p.bar(6, 14, 8, 15, c.boots, 0, 2);
      p.bar(9, 13, 11, 14, c.boots, 0, 3);
    }
  } else {
    const farBack = frame === 0;
    p.bar(5, 12, 6, 13, c.trousers, farBack ? 0 : 1, farBack ? 2 : 3);
    p.bar(3, 14, 5, 15, c.boots, 0, farBack ? 2 : 3);
    p.bar(10, 12, 11, 13, c.trousers, farBack ? 1 : 0, farBack ? 3 : 2);
    p.bar(11, 14, 13, 15, c.boots, 0, farBack ? 3 : 2);
  }
  p.bar(6, 8 + bob, 10, 11, c.shirt, 1, 4);
  p.fill(6, 11, 10, 11, c.trousers[0]);
  if (frame === 0) {
    p.bar(4, 8 + bob, 5, 9 + bob, c.shirt, 0, 3);
    p.set(4, 10 + bob, c.skin[2]);
  } else if (frame === 2) {
    p.bar(11, 8 + bob, 12, 9 + bob, c.shirt, 1, 4);
    p.set(13, 9 + bob, c.skin[3]);
  } else {
    p.bar(8, 8 + bob, 9, 10 + bob, c.shirt, 0, 3);
    p.set(8, 11 + bob, c.skin[2]);
  }
  p.dome(8.9, 5.6 + bob, 3.3, 2.6, c.skin, 1, 4, (_x, y) => y >= 4 + bob);
  p.fill(10, 5 + bob, 10, 6 + bob, c.ink);
  p.set(12, 5 + bob, c.skin[3]);
  p.set(11, 7 + bob, c.skin[0]);
  p.dome(8.5, 3.4 + bob, 4.5, 3.2, c.helmet, 1, 4, (_x, y) => y <= 2 + bob);
  p.bar(3, 3 + bob, 14, 3 + bob, c.helmet, 0, 3);
  lamp(p, c, 11, 0);
  p.outline(c.ink);
  return p;
}
